// Ecrane de autentificare și utilitare: login/înregistrare, resetare parolă,
// configurare profil inițial și ecranul de debug (/debug).
import React, { useState, useEffect } from "react";
import { fmAlert, fmConfirm } from './dialog';
import { calcAge, isValidPhone, validatePassword } from './helpers';
import { KINSHIP } from './data';
import { DateField, DatePicker, IconTrash, PasswordRules, PhoneInput, SearchableSelect, AddressPicker } from './ui';
import { getLogs, clearLogs } from './logger';
import { CSS } from './styles';
import { signUp, signIn, signInWithProvider, sendPasswordReset, updatePassword, sendMfaCode, verifyMfaCode } from './supabase';
import { t } from './i18n';

// ─── DEBUG SCREEN ────────────────────────────────────────────────────────────
export function DebugScreen() {
  const [logs, setLogs] = useState(getLogs());
  const [filter, setFilter] = useState("all"); // all | error
  function refresh() { setLogs(getLogs()); }
  useEffect(() => {
    const id = setInterval(refresh, 1500); // auto-refresh la 1.5s
    return () => clearInterval(id);
  }, []);
  const shown = filter==="error" ? logs.filter(l=>l.level==="error") : logs;
  const errorCount = logs.filter(l=>l.level==="error").length;
  function copyAll() {
    const text = logs.map(l=>`[${l.t}] ${l.level.toUpperCase()}: ${l.msg}`).join("\n");
    try {
      navigator.clipboard.writeText(text);
      fmAlert("Log-uri copiate! Le poți lipi oriunde (ex. într-un mesaj).");
    } catch(e) {
      // fallback dacă clipboard nu merge
      fmAlert(text.slice(0,3000));
    }
  }
  function fmtTime(iso) {
    try { const d=new Date(iso); return d.toLocaleTimeString("ro-RO",{hour:"2-digit",minute:"2-digit",second:"2-digit"})+"."+String(d.getMilliseconds()).padStart(3,"0"); }
    catch(e){ return iso; }
  }
  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",color:"var(--text)",fontFamily:"monospace",padding:16}}>
      <style>{CSS}</style>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:18,fontWeight:700,fontFamily:"'Palatino Linotype',serif"}}>🐛 Debug · {logs.length} log-uri {errorCount>0&&<span style={{color:"var(--red)"}}>({errorCount} erori)</span>}</div>
        <button className="bg" style={{fontSize:12}} onClick={()=>{ window.location.href = window.location.origin; }}>{t("← Înapoi în aplicație")}</button>
      </div>
      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
        <button className={filter==="all"?"bp":"bg"} style={{fontSize:12}} onClick={()=>setFilter("all")}>{t("Toate")}</button>
        <button className={filter==="error"?"bp":"bg"} style={{fontSize:12}} onClick={()=>setFilter("error")}>{t("Doar erori")}</button>
        <button className="bg" style={{fontSize:12}} onClick={refresh}>{t("↻ Reîmprospătează")}</button>
        <button className="bp" style={{fontSize:12}} onClick={copyAll}>{t("📋 Copiază tot")}</button>
        <button className="bdel" style={{fontSize:12,border:"1px solid #3d0f0f",borderRadius:8,padding:"7px 14px"}} onClick={()=>fmConfirm("Ștergi toate log-urile?",{danger:true,okText:"Șterge"}).then(ok=>{ if(ok){ clearLogs(); refresh(); } })}><IconTrash size={13}/>{t(" Șterge tot")}</button>
      </div>
      {shown.length===0 ? (
        <div style={{textAlign:"center",color:"var(--text-4)",padding:"48px 0"}}>{t("Niciun log încă. Folosește aplicația (ex. încarcă un fișier) și revino aici.")}</div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:3}}>
          {[...shown].reverse().map((l,i)=>(
            <div key={i} style={{padding:"7px 10px",borderRadius:6,fontSize:12,lineHeight:1.5,wordBreak:"break-word",
              background:l.level==="error"?"#1a0808":"var(--surface)",
              border:`1px solid ${l.level==="error"?"var(--red-bg)":"var(--border)"}`}}>
              <span style={{color:"var(--text-4)",marginRight:8}}>{fmtTime(l.t)}</span>
              <span style={{color:l.level==="error"?"var(--red)":"var(--blue)",fontWeight:700,marginRight:8}}>{l.level==="error"?"EROARE":"LOG"}</span>
              <span style={{color:l.level==="error"?"var(--red-2)":"var(--text)"}}>{l.msg}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── RESET PASSWORD SCREEN ───────────────────────────────────────────────────
export function ResetPasswordScreen({onDone}) {
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function handleReset() {
    const pwErr = validatePassword(pass);
    if(pwErr){ setError(pwErr); return; }
    if(pass!==pass2){ setError("Parolele nu coincid."); return; }
    setLoading(true); setError("");
    const res = await updatePassword(pass);
    setLoading(false);
    if(res.ok){ setDone(true); }
    else { setError(res.message || "Eroare la schimbarea parolei. Linkul poate fi expirat — cere unul nou."); }
  }

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <style>{CSS}</style>
      <div style={{width:"100%",maxWidth:400,background:"var(--surface)",border:"1px solid var(--border)",borderRadius:20,padding:36}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{fontSize:44,marginBottom:12,color:"var(--red)"}}>♥</div>
          <div style={{fontSize:22,fontWeight:700,marginBottom:6}}>{t("Parolă nouă")}</div>
          <div style={{fontSize:13,color:"var(--text-3)"}}>{done?t("Gata!"):t("Alege o parolă nouă pentru contul tău")}</div>
        </div>
        {done ? (
          <div>
            <div style={{marginBottom:18,padding:"12px 14px",background:"var(--green-bg-soft)",border:"1px solid var(--green-line)",borderRadius:8,fontSize:13,color:"var(--green)",textAlign:"center"}}>
              {t("Parola a fost schimbată cu succes.")}
            </div>
            <button className="bp" style={{width:"100%",padding:"12px",fontSize:15}} onClick={()=>{ window.location.href = window.location.origin; }}>
              {t("Intră în cont")}
            </button>
          </div>
        ) : (
          <div>
            <div style={{marginBottom:14}}>
              <div className="lbl">{t("Parolă nouă")}</div>
              <div style={{position:"relative"}}>
                <input type={showPass?"text":"password"} value={pass} onChange={e=>setPass(e.target.value)} placeholder="" style={{paddingRight:42}}/>
                <button type="button" onClick={()=>setShowPass(s=>!s)} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:16,padding:4}} title={showPass?t("Ascunde"):t("Arată")}>{showPass?"🙈":"👁"}</button>
              </div>
              <PasswordRules/>
            </div>
            <div style={{marginBottom:20}}>
              <div className="lbl">{t("Confirmă parola")}</div>
              <input type={showPass?"text":"password"} value={pass2} onChange={e=>setPass2(e.target.value)} placeholder={t("Repetă parola")} onKeyDown={e=>e.key==="Enter"&&handleReset()}/>
            </div>
            {error&&<div style={{marginBottom:14,padding:"10px 14px",background:"var(--red-bg-soft)",border:"1px solid var(--red-line)",borderRadius:8,fontSize:13,color:"var(--red-2)"}}>{t(error)}</div>}
            <button className="bp" style={{width:"100%",padding:"12px",fontSize:15}} onClick={handleReset} disabled={loading}>
              {loading?t("Se salvează..."):t("Schimbă parola")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── GATE 2FA ─────────────────────────────────────────────────────────────────
// Afișat după login, când userul are 2FA pe email activ. Trimite un cod pe email
// și cere introducerea lui înainte de a intra în aplicație.
export function MfaGateScreen({session, onVerified, onCancel}) {
  const [code, setCode] = useState("");
  const [mfaToken, setMfaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const send = React.useCallback(async ()=>{
    setSending(true); setError(""); setInfo("");
    const res = await sendMfaCode(session);
    setSending(false);
    if(res.ok){ setMfaToken(res.token); setInfo("Ți-am trimis un cod pe email."); }
    else setError(res.message || "Nu am putut trimite codul. Încearcă din nou.");
  }, [session]);
  useEffect(()=>{ send(); }, [send]);

  async function submit(e){
    if(e&&e.preventDefault) e.preventDefault();
    const c = code.replace(/\s/g,"");
    if(c.length<6){ setError("Introdu codul din 6 cifre."); return; }
    if(!mfaToken){ setError("Nu există un cod activ. Apasă Retrimite codul."); return; }
    setLoading(true); setError("");
    const res = await verifyMfaCode(session, mfaToken, c);
    setLoading(false);
    if(res.ok){ onVerified(); }
    else if(res.error==="expired") setError("Codul a expirat. Apasă Retrimite codul.");
    else setError(res.message || "Cod greșit. Verifică emailul și încearcă din nou.");
  }
  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <style>{CSS}</style>
      <div style={{width:"100%",maxWidth:400,background:"var(--surface)",border:"1px solid var(--border)",borderRadius:20,padding:36}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{fontSize:44,marginBottom:12}}>🔐</div>
          <div style={{fontSize:22,fontWeight:700,marginBottom:6}}>{t("Verificare în 2 pași")}</div>
          <div style={{fontSize:13,color:"var(--text-3)",lineHeight:1.5}}>{t("Ți-am trimis un cod de 6 cifre pe ")}<strong>{session?.user?.email||t("emailul tău")}</strong>{t(". Introdu-l mai jos.")}</div>
        </div>
        <form onSubmit={submit}>
          <input autoFocus inputMode="numeric" maxLength={6} value={code}
            onChange={e=>setCode(e.target.value.replace(/[^0-9]/g,""))}
            placeholder={t("000000")}
            style={{width:"100%",textAlign:"center",fontSize:26,letterSpacing:"0.4em",fontFamily:"monospace",padding:"12px 0"}}/>
          {info&&!error&&<div style={{fontSize:12,color:"var(--green)",marginTop:10,textAlign:"center"}}>{t(info)}</div>}
          {error&&<div style={{fontSize:12.5,color:"var(--red)",marginTop:10,textAlign:"center"}}>{t(error)}</div>}
          <button type="submit" className="bp" disabled={loading||sending} style={{width:"100%",marginTop:18,padding:"12px"}}>{loading?t("Se verifică…"):t("Confirmă")}</button>
        </form>
        <button className="bg" style={{width:"100%",marginTop:10}} disabled={sending} onClick={send}>{sending?t("Se trimite…"):t("↻ Retrimite codul")}</button>
        <button className="bg" style={{width:"100%",marginTop:8}} onClick={onCancel}>{t("← Ieși din cont")}</button>
      </div>
    </div>
  );
}

// ─── LOGIN SCREEN ─────────────────────────────────────────────────────────────
export function LoginScreen({onLogin}) {
  const[email,setEmail]=useState("");
  const[pass,setPass]=useState("");
  const[showPass,setShowPass]=useState(false);
  const[name,setName]=useState("");
  const[dob,setDob]=useState("");
  const[sex,setSex]=useState("");
  const[phone,setPhone]=useState("");
  const[emergency,setEmergency]=useState([{name:"",phone:"",relation:""}]);
  const[mode,setMode]=useState("login");
  // Bifa de consimțământ e ascunsă până sunt gata politicile. Pune true ca s-o reactivezi.
  const REQUIRE_CONSENT = false;
  const POLICY_VERSION = "2026-01"; // actualizează când schimbi politicile
  const[consent,setConsent]=useState(false);
  const[loading,setLoading]=useState(false);
  const[error,setError]=useState("");
  const[success,setSuccess]=useState("");

  function updateEmergency(i,field,val){ setEmergency(prev=>prev.map((e,j)=>j===i?{...e,[field]:val}:e)); }
  function addEmergency(){ setEmergency(prev=>[...prev,{name:"",phone:"",relation:""}]); }
  function removeEmergency(i){ setEmergency(prev=>prev.filter((_,j)=>j!==i)); }

  async function handleSubmit() {
    if(!email||!pass){setError("Completează email și parolă.");return;}
    if(mode==="signup"){
      const pwErr = validatePassword(pass);
      if(pwErr){setError(pwErr);return;}
      if(!name.trim()){setError("Completează numele.");return;}
      if(!dob.trim()){setError("Completează data nașterii.");return;}
      if(!sex){setError("Selectează sexul.");return;}
      if(!phone.trim()){setError("Completează numărul de telefon.");return;}
      if(!isValidPhone(phone)){setError("Numărul de telefon trebuie să aibă 10 cifre.");return;}
      const validEmergency = emergency.filter(ec=>ec.name.trim() && ec.phone.trim());
      if(validEmergency.length===0){setError("Adaugă cel puțin o persoană de urgență (nume + telefon).");return;}
      const badPhone = validEmergency.find(ec=>!isValidPhone(ec.phone));
      if(badPhone){setError("Telefonul persoanei de urgență trebuie să aibă 10 cifre.");return;}
      if(REQUIRE_CONSENT && !consent){setError("Trebuie să accepți Termenii și Politica de confidențialitate pentru a crea un cont.");return;}
    }
    setLoading(true);setError("");setSuccess("");
    try {
      if(mode==="signup"){
        const validEmergency = emergency.filter(ec=>ec.name.trim() && ec.phone.trim());
        await signUp(email, pass, {
          full_name:name.trim(),
          dob:dob.trim(),
          sex,
          phone:phone.trim(),
          emergencyContacts:validEmergency,
          // Dovada consimțământului (salvată la cont) — se completează doar când bifa e activă
          ...(REQUIRE_CONSENT && consent ? { consent:{ accepted:true, at:new Date().toISOString(), version:POLICY_VERSION } } : {})
        });
        setSuccess("Cont creat! Verifică emailul și confirmă contul, apoi loghează-te.");
        setMode("login");
      } else {
        const data = await signIn(email,pass);
        onLogin(data.session);
      }
    } catch(e) {
      if(e.message?.includes("Email not confirmed")) setError("Verifică emailul și confirmă contul înainte de a te loga.");
      else if(e.message?.includes("Invalid login")) setError("Email sau parolă incorectă.");
      else if(e.message?.includes("already registered")) setError("Email deja înregistrat. Loghează-te.");
      else setError(e.message||"Eroare necunoscută.");
    }
    setLoading(false);
  }

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <style>{CSS}</style>
      <div style={{width:"100%",maxWidth:400,background:"var(--surface)",border:"1px solid var(--border)",borderRadius:20,padding:36}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:48,marginBottom:12,color:"var(--red)"}}>♥</div>
          <div style={{fontSize:24,fontWeight:700,marginBottom:6}}>{t("Fișă Medicală")}</div>
          <div style={{fontSize:13,color:"var(--text-3)"}}>{mode==="login"?t("Loghează-te pentru a accesa datele"):t("Creează un cont nou")}</div>
        </div>
        {mode==="signup"&&(
          <>
            <div style={{marginBottom:14}}><div className="lbl">{t("Nume complet *")}</div><input value={name} onChange={e=>setName(e.target.value)} placeholder={t("ex: Cristina Hașaș")}/></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
              <div>
                <div className="lbl">{t("Data nașterii *")}</div>
                <DatePicker value={dob} onChange={setDob}/>
              </div>
              <div>
                <div className="lbl">{t("Vârstă")}</div>
                <div style={{fontSize:18,fontWeight:700,color:"var(--blue)",paddingTop:6}}>{dob&&calcAge(dob)!==""?`${calcAge(dob)} ani`:<span style={{color:"var(--border-2)",fontSize:14,fontWeight:400}}>—</span>}</div>
              </div>
            </div>
            <div style={{marginBottom:14}}>
              <div className="lbl">{t("Sex *")}</div>
              <div style={{display:"flex",gap:8}}>
                {[["M","Masculin"],["F","Feminin"]].map(([v,l])=>(
                  <button key={v} type="button" onClick={()=>setSex(v)}
                    style={{flex:1,padding:"8px",border:`1px solid ${sex===v?"#7c3aed":"var(--border-2)"}`,background:sex===v?"var(--blue-bg)":"none",borderRadius:8,color:"var(--text)",cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>{l}</button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:14}}><div className="lbl">{t("Telefon personal *")}</div><PhoneInput value={phone} onChange={setPhone}/></div>
            <div style={{marginBottom:14,padding:"12px",background:"var(--bg)",borderRadius:8,border:"1px solid var(--border)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div className="lbl" style={{color:"var(--red)",margin:0}}>{t("⚠ Persoane de urgență *")}</div>
                <button type="button" className="bg" style={{fontSize:11,padding:"3px 10px"}} onClick={addEmergency}>{t("+ Adaugă")}</button>
              </div>
              {emergency.map((ec,i)=>(
                <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:i<emergency.length-1?10:0,position:"relative"}}>
                  <div style={{gridColumn:"span 2"}}><input value={ec.name} onChange={e=>updateEmergency(i,"name",e.target.value)} placeholder={t("Nume")}/></div>
                  <div><PhoneInput value={ec.phone} onChange={v=>updateEmergency(i,"phone",v)}/></div>
                  <div style={{display:"flex",gap:6}}>
                    <div style={{flex:1}}><SearchableSelect options={KINSHIP} sort={false} value={ec.relation||""} onChange={v=>updateEmergency(i,"relation",v)} placeholder={t("Rudenie")}/></div>
                    {emergency.length>1&&<button type="button" className="bdel" onClick={()=>removeEmergency(i)}><IconTrash/></button>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div style={{marginBottom:14}}><div className="lbl">{t("Email")}{mode==="signup"?" *":""}</div><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder={t("adresa@email.com")} onKeyDown={e=>mode==="login"&&e.key==="Enter"&&handleSubmit()}/></div>
        <div style={{marginBottom:mode==="login"?8:14}}><div className="lbl">{t("Parolă")}{mode==="signup"?" *":""}</div>
          <div style={{position:"relative"}}>
            <input type={showPass?"text":"password"} value={pass} onChange={e=>setPass(e.target.value)} placeholder="" onKeyDown={e=>e.key==="Enter"&&handleSubmit()} style={{width:"100%",paddingRight:42}}/>
            <button type="button" onClick={()=>setShowPass(v=>!v)} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:16,padding:4}} title={showPass?t("Ascunde"):t("Arată")}>{showPass?"🙈":"👁"}</button>
          </div>
          {mode==="signup"&&<PasswordRules/>}
        </div>
        {mode==="login"&&(
          <div style={{textAlign:"right",marginBottom:16}}>
            <button type="button" onClick={async()=>{
              if(!email){ setError("Scrie întâi adresa de email, apoi apasă „Am uitat parola”."); return; }
              setError(""); setSuccess("");
              const res = await sendPasswordReset(email);
              if(res.ok) setSuccess("Ți-am trimis un email cu instrucțiuni de resetare a parolei. Verifică inboxul.");
              else setError(res.message || "Nu am putut trimite emailul de resetare.");
            }} style={{background:"none",border:"none",color:"var(--blue)",cursor:"pointer",fontSize:12,fontFamily:"inherit",padding:0}}>
              Am uitat parola
            </button>
          </div>
        )}
        {mode==="signup"&&REQUIRE_CONSENT&&(
          <label style={{display:"flex",alignItems:"flex-start",gap:10,margin:"4px 0 16px",cursor:"pointer",fontSize:12.5,color:"var(--text-2)",lineHeight:1.5}}>
            <input type="checkbox" checked={consent} onChange={e=>setConsent(e.target.checked)} style={{marginTop:2,flexShrink:0,width:18,height:18,cursor:"pointer"}}/>
            <span>{t("Am citit și accept ")}<strong>{t("Termenii și condițiile")}</strong>, <strong>{t("Politica de confidențialitate (GDPR)")}</strong> și <strong>{t("Politica de cookie-uri")}</strong>{t(". Am înțeles că aplicația nu oferă sfaturi medicale.")}</span>
          </label>
        )}
        {error&&<div style={{marginBottom:14,padding:"10px 14px",background:"var(--red-bg-soft)",border:"1px solid var(--red-line)",borderRadius:8,fontSize:13,color:"var(--red-2)"}}>{t(error)}</div>}
        {success&&<div style={{marginBottom:14,padding:"10px 14px",background:"var(--green-bg-soft)",border:"1px solid var(--green-line)",borderRadius:8,fontSize:13,color:"var(--green)"}}>{t(success)}</div>}
        <button className="bp" style={{width:"100%",padding:"12px",fontSize:15,marginBottom:14}} onClick={handleSubmit} disabled={loading}>
          {loading?t("Se procesează..."):mode==="login"?t("Intră în cont"):t("Creează cont")}
        </button>
        <div style={{display:"flex",alignItems:"center",gap:10,margin:"4px 0 14px",color:"var(--text-4)",fontSize:12}}>
          <div style={{flex:1,height:1,background:"var(--border)"}}/>{t("sau")}<div style={{flex:1,height:1,background:"var(--border)"}}/>
        </div>
        <button type="button" onClick={async()=>{ setError(""); const res=await signInWithProvider('google'); if(!res.ok)setError(res.message||"Eroare la autentificarea cu Google."); }}
          style={{width:"100%",padding:"11px",fontSize:14,marginBottom:14,background:"#fff",color:"#1f2937",border:"none",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          {t("Continuă cu Google")}
        </button>
        {/* Buton Apple ascuns până la configurarea în Apple Developer + Supabase.
            Pentru reactivare, decomentează blocul de mai jos:
        <button type="button" onClick={async()=>{ setError(""); const res=await signInWithProvider('apple'); if(!res.ok)setError(res.message||"Eroare la autentificarea cu Apple."); }}
          style={{width:"100%",padding:"11px",fontSize:14,marginBottom:14,background:"#000",color:"#fff",border:"1px solid var(--border-2)",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M17.05 12.04c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.09-2.01-3.76-2.04-1.6-.16-3.12.94-3.93.94-.81 0-2.06-.92-3.39-.89-1.74.03-3.35 1.01-4.25 2.57-1.81 3.14-.46 7.79 1.3 10.34.86 1.25 1.89 2.65 3.23 2.6 1.3-.05 1.79-.84 3.36-.84 1.57 0 2.01.84 3.38.81 1.4-.02 2.28-1.27 3.13-2.53.99-1.45 1.4-2.85 1.42-2.93-.03-.01-2.72-1.04-2.75-4.13zM14.53 4.5c.72-.87 1.2-2.08 1.07-3.28-1.03.04-2.28.69-3.02 1.56-.66.77-1.24 2-1.08 3.18 1.15.09 2.32-.59 3.03-1.46z"/></svg>
          Continuă cu Apple
        </button>
        */}
        <div style={{textAlign:"center",fontSize:13,color:"var(--text-3)"}}>
          {mode==="login"
            ?<span>{t("Nu ai cont?")} <button onClick={()=>{setMode("signup");setError("");setSuccess("");}} style={{background:"none",border:"none",color:"var(--blue)",cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>{t("Înregistrează-te")}</button></span>
            :<span>{t("Ai cont?")} <button onClick={()=>{setMode("login");setError("");setSuccess("");}} style={{background:"none",border:"none",color:"var(--blue)",cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>{t("Loghează-te")}</button></span>
          }
        </div>
      </div>
    </div>
  );
}

// ─── PROFILE SETUP (după login OAuth) ────────────────────────────────────────
export function ProfileSetupScreen({session, onComplete, onSignOut}) {
  const meta = session?.user?.user_metadata || {};
  const [name, setName] = useState(meta.full_name || meta.name || "");
  const [dob, setDob] = useState(meta.dob || "");
  const [sex, setSex] = useState(meta.sex || "");
  const [phone, setPhone] = useState(meta.phone || "");
  const [city, setCity] = useState(meta.city || "");
  const [county, setCounty] = useState(meta.county || "");
  const [country, setCountry] = useState(meta.country || "România");
  const [emergency, setEmergency] = useState(Array.isArray(meta.emergencyContacts)&&meta.emergencyContacts.length ? meta.emergencyContacts : [{name:"",phone:"",relation:""}]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const age = dob ? calcAge(dob) : null;

  function addEmergency(){ setEmergency(p=>[...p,{name:"",phone:"",relation:""}]); }
  function updEmergency(i,field,val){ setEmergency(p=>p.map((e,idx)=>idx===i?{...e,[field]:val}:e)); }
  function removeEmergency(i){ setEmergency(p=>p.filter((_,idx)=>idx!==i)); }

  async function handleSave(){
    if(!name.trim()){ setError("Completează numele."); return; }
    if(!dob.trim()){ setError("Completează data nașterii."); return; }
    if(!sex){ setError("Selectează sexul."); return; }
    if(!phone.trim()){ setError("Completează numărul de telefon."); return; }
    if(!isValidPhone(phone)){ setError("Numărul de telefon trebuie să aibă 10 cifre."); return; }
    if(!city.trim()){ setError("Completează localitatea."); return; }
    if(!county.trim()){ setError("Completează județul."); return; }
    if(!country.trim()){ setError("Completează țara."); return; }
    const validEmergency = emergency.filter(e=>e.name.trim() && e.phone.trim());
    if(validEmergency.length===0){ setError("Adaugă cel puțin o persoană de contact în caz de urgență."); return; }
    setError(""); setBusy(true);
    await onComplete({name:name.trim(),dob:dob.trim(),sex,phone:phone.trim(),city:city.trim(),county:county.trim(),country:country.trim(),emergencyContacts:validEmergency});
  }

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:20,overflowY:"auto"}}>
      <style>{CSS}</style>
      <div style={{width:"100%",maxWidth:440,background:"var(--surface)",border:"1px solid var(--border)",borderRadius:20,padding:32,margin:"20px 0"}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{fontSize:40,marginBottom:10,color:"var(--blue)"}}>♥</div>
          <div style={{fontSize:20,fontWeight:700,marginBottom:6}}>{t("Bine ai venit!")}</div>
          <div style={{fontSize:13,color:"var(--text-3)"}}>{t("Completează datele tale pentru a începe. Toate câmpurile sunt obligatorii.")}</div>
        </div>

        <div style={{marginBottom:14}}><div className="lbl">{t("Nume complet *")}</div><input value={name} onChange={e=>setName(e.target.value)} placeholder={t("Prenume Nume")}/></div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div><div className="lbl">Data nașterii *{age!=null&&<span style={{color:"var(--blue)",marginLeft:6}}>({age} ani)</span>}</div><DateField value={dob} onChange={setDob}/></div>
          <div><div className="lbl">{t("Sex *")}</div>
            <div style={{display:"flex",gap:6}}>
              <button type="button" onClick={()=>setSex("M")} style={{flex:1,padding:"8px",borderRadius:8,fontFamily:"inherit",fontSize:13,cursor:"pointer",background:sex==="M"?"var(--blue-bg)":"var(--bg)",border:`1px solid ${sex==="M"?"#7c3aed":"var(--border-2)"}`,color:sex==="M"?"var(--blue-2)":"var(--text-2)"}}>{t("M")}</button>
              <button type="button" onClick={()=>setSex("F")} style={{flex:1,padding:"8px",borderRadius:8,fontFamily:"inherit",fontSize:13,cursor:"pointer",background:sex==="F"?"var(--blue-bg)":"var(--bg)",border:`1px solid ${sex==="F"?"#7c3aed":"var(--border-2)"}`,color:sex==="F"?"var(--blue-2)":"var(--text-2)"}}>{t("F")}</button>
            </div>
          </div>
        </div>

        <div style={{marginBottom:14}}><div className="lbl">{t("Telefon *")}</div><input value={phone} onChange={e=>setPhone(e.target.value)} placeholder={t("07XXXXXXXX")} inputMode="numeric"/></div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <AddressPicker city={city} county={county} onChange={({city,county})=>{setCity(city);setCounty(county);}} localityLabel={t("Localitate *")} countyLabel={t("Județ *")}/>
        </div>
        <div style={{marginBottom:18}}><div className="lbl">{t("Țară *")}</div><input value={country} onChange={e=>setCountry(e.target.value)} placeholder={t("ex: România")}/></div>

        <div style={{marginBottom:18}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div className="lbl" style={{margin:0}}>{t("Persoană de contact în urgență *")}</div>
            <button type="button" className="bg" style={{fontSize:11,padding:"3px 10px"}} onClick={addEmergency}>{t("+ Adaugă")}</button>
          </div>
          {emergency.map((ec,i)=>(
            <div key={i} style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10,padding:10,background:"var(--bg)",borderRadius:8,border:"1px solid var(--border)"}}>
              <div style={{display:"flex",gap:6}}>
                <input value={ec.name} onChange={e=>updEmergency(i,"name",e.target.value)} placeholder={t("Nume")} style={{flex:1}}/>
                {emergency.length>1&&<button type="button" className="bg" style={{padding:"0 10px",color:"var(--red)"}} onClick={()=>removeEmergency(i)}>✕</button>}
              </div>
              <input value={ec.phone} onChange={e=>updEmergency(i,"phone",e.target.value)} placeholder={t("Telefon")} inputMode="numeric"/>
              <SearchableSelect options={KINSHIP} sort={false} value={ec.relation||""} onChange={v=>updEmergency(i,"relation",v)} placeholder={t("Grad de rudenie...")}/>
            </div>
          ))}
        </div>

        {error&&<div style={{marginBottom:14,padding:"10px 14px",background:"var(--red-bg-soft)",border:"1px solid var(--red-line)",borderRadius:8,fontSize:13,color:"var(--red-2)"}}>{t(error)}</div>}

        <button className="bp" style={{width:"100%",padding:"12px",fontSize:15}} onClick={handleSave} disabled={busy}>
          {busy?t("Se salvează..."):t("Salvează și începe")}
        </button>
        <button type="button" onClick={onSignOut} style={{width:"100%",marginTop:10,background:"none",border:"none",color:"var(--text-3)",cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>{t("Ieși din cont")}</button>
      </div>
    </div>
  );
}
